import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useEntity,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import {
  RELATION_HAS_MEMBER,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { makeStyles } from 'tss-react/mui';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import type { StatusLevel } from '@mercedes-benz/odp-theme-v2';
import {
  CogWheelIcon,
  getColors,
  OwnerIcon,
  TagIcon,
  typography,
} from '@mercedes-benz/odp-theme-v2';
import DomainIcon from '@mui/icons-material/Domain';
import BuildIcon from '@mui/icons-material/Build';
import { OwnerLink } from '../components/OwnerLink';

const STATUS_CONFIG: Record<StatusLevel, { color: string; label: string }> = {
  healthy: { color: '#198025', label: 'Operational' },
  degraded: { color: '#FACC00', label: 'Partial Outage' },
  critical: { color: '#D92121', label: 'Major Outage' },
  unknown: { color: '#666666', label: 'Unknown' },
};

interface InsightItem {
  name: string;
  status: StatusLevel;
}

interface InsightCategory {
  heading: string;
  items: InsightItem[];
}

const INSIGHTS: InsightCategory[] = [
  {
    heading: 'Core',
    items: [{ name: 'Authentication Service', status: 'healthy' }],
  },
  {
    heading: 'Integrations',
    items: [
      { name: 'Git Integration', status: 'healthy' },
      { name: 'CI/CD Integration', status: 'critical' },
      { name: 'Kubernetes Integration', status: 'healthy' },
      { name: 'Monitoring Integration', status: 'critical' },
    ],
  },
  {
    heading: 'Integrations',
    items: [
      { name: 'TechDocs Service', status: 'healthy' },
      { name: 'Software Templates', status: 'degraded' },
      { name: 'Notification Service', status: 'healthy' },
      { name: 'Search Index Service', status: 'healthy' },
    ],
  },
  {
    heading: 'Infrastructure',
    items: [
      { name: 'Database Cluster', status: 'critical' },
      { name: 'API Explorer', status: 'degraded' },
      { name: 'Services Architecture', status: 'healthy' },
    ],
  },
];

const AVATAR_COLOR = '#0078D6';

function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
    },
    columns: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 24,
      '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
      },
    },
    card: {
      background: c.background.paper,
      borderRadius: 24,
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
    },
    cardTitle: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 700,
      fontSize: 20,
      lineHeight: '28px',
      color: c.text.primary,
      margin: '0 0 12px 0',
    },

    /* ---- Basic Information ---- */
    descriptionLabel: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 700,
      fontSize: 16,
      lineHeight: '24px',
      color: c.text.primary,
      margin: '0 0 8px 0',
    },
    description: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: c.text.primary,
      margin: 0,
    },
    metadataStrip: {
      display: 'flex',
      flexDirection: 'column',
      marginTop: 24,
    },
    metadataItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '5px 0',
    },
    metadataHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      width: 120,
      flexShrink: 0,
      '& svg': {
        width: 16,
        height: 16,
        color: c.text.secondary,
      },
    },
    metadataLabel: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 600,
      fontSize: 13,
      lineHeight: '20px',
      color: c.text.secondary,
    },
    metadataValue: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '20px',
      color: c.text.primary,
      flex: 1,
    },
    metadataLink: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 400,
      fontSize: 16,
      lineHeight: '24px',
      color: c.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    metadataValueLink: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '20px',
      color: c.primary.main,
      flex: 1,
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    tagsSection: {
      marginTop: 'auto',
      paddingTop: 24,
    },
    tagsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginBottom: 8,
      '& svg': {
        width: 16,
        height: 16,
        color: c.text.secondary,
      },
    },
    tagsRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
    },
    tag: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '18px',
      color: c.text.primary,
      background: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.16)',
      borderRadius: 9999,
      height: 32,
    },

    /* ---- Technical Insights ---- */
    categoryHeading: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontWeight: 700,
      fontSize: 16,
      lineHeight: '24px',
      color: c.text.primary,
      margin: '12px 0 4px 0',
      paddingLeft: 2,
      '&:first-of-type': {
        marginTop: 0,
      },
    },
    insightRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 0',
      paddingLeft: 15,
    },
    insightName: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '24px',
      color: c.text.primary,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusInline: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 6,
      flexShrink: 0,
    },
    statusLabel: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: 16,
      fontWeight: 700,
      lineHeight: '24px',
      color: c.text.primary,
    },
    statusDot: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      flexShrink: 0,
    },

    /* ---- Team Members ---- */
    teamGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: '12px 24px',
    },
    memberCard: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      cursor: 'pointer',
      borderRadius: 8,
      padding: '4px 8px',
      margin: '-4px -8px',
      textDecoration: 'none',
      transition: 'background 0.12s ease',
      '&:hover': {
        background: fgAlpha(0.05),
      },
    },
    avatar: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.75rem',
      fontWeight: 600,
      fontFamily: typography.fontFamilyCorpoSText,
      color: '#FFFFFF',
      flexShrink: 0,
    },
    memberInfo: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    memberName: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.primary,
      fontSize: '0.8rem',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    memberRole: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: fgAlpha(0.45),
      fontSize: '0.7rem',
    },
    moreButton: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      cursor: 'pointer',
      borderRadius: 8,
      padding: '4px 8px',
      margin: '-4px -8px',
      textDecoration: 'none',
      transition: 'background 0.12s ease',
      background: 'none',
      border: 'none',
      '&:hover': {
        background: fgAlpha(0.05),
      },
    },
    moreAvatar: {
      width: 36,
      height: 36,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.85rem',
      fontWeight: 600,
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.secondary,
      background: fgAlpha(0.1),
      flexShrink: 0,
    },
    moreText: {
      fontFamily: typography.fontFamilyCorpoSText,
      color: c.text.link,
      fontSize: '0.8rem',
      fontWeight: 500,
    },
  };
});

const MAX_MEMBERS_DISPLAY = 14;
const MEMBERS_FETCH_LIMIT = MAX_MEMBERS_DISPLAY + 1;

export const CoreDetailsTab = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [members, setMembers] = useState<Entity[]>([]);
  const [totalMemberCount, setTotalMemberCount] = useState(0);

  const tags = entity.metadata.tags || [];

  useEffect(() => {
    let cancelled = false;
    const ownerRef = entity.spec?.owner as string | undefined;

    if (ownerRef) {
      (async () => {
        try {
          const parsed = parseEntityRef(ownerRef, {
            defaultKind: 'group',
            defaultNamespace: 'default',
          });
          const ownerEntityRef = stringifyEntityRef(parsed);
          const { items } = await catalogApi.getEntities({
            filter: { kind: 'group', 'metadata.name': parsed.name },
          });
          const ownerEntity = items.find(
            e => stringifyEntityRef(e) === ownerEntityRef,
          );
          if (!ownerEntity || cancelled) return;

          const memberRefs = getEntityRelations(
            ownerEntity,
            RELATION_HAS_MEMBER,
          );
          if (memberRefs.length === 0) return;

          setTotalMemberCount(memberRefs.length);

          const refsToFetch = memberRefs
            .slice(0, MEMBERS_FETCH_LIMIT)
            .map(r => stringifyEntityRef(r));

          const { items: memberEntities } = await catalogApi.getEntitiesByRefs({
            entityRefs: refsToFetch,
          });
          if (!cancelled) {
            setMembers(
              memberEntities.filter((e): e is Entity => e !== undefined),
            );
          }
        } catch {
          // ignore fetch errors
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [catalogApi, entity.spec?.owner]);

  return (
    <div className={classes.root}>
      <div className={classes.columns}>
        {/* ---- Basic Information ---- */}
        <div className={classes.card}>
          <h3 className={classes.cardTitle}>Basic Information</h3>

          {entity.metadata.description && (
            <>
              <p className={classes.descriptionLabel}>Description</p>
              <p className={classes.description}>
                {entity.metadata.description}
              </p>
            </>
          )}

          <div className={classes.metadataStrip}>
            <div className={classes.metadataItem}>
              <div className={classes.metadataHeader}>
                <CogWheelIcon />
                <span className={classes.metadataLabel}>Kind</span>
              </div>
              <Link
                to={`/catalog?kind=${encodeURIComponent(entity.kind)}`}
                className={classes.metadataValueLink}
              >
                {entity.kind}
              </Link>
            </div>

            {entity.spec?.type && (
              <div className={classes.metadataItem}>
                <div className={classes.metadataHeader}>
                  <BuildIcon />
                  <span className={classes.metadataLabel}>Type</span>
                </div>
                <Link
                  to={`/catalog?type=${encodeURIComponent(entity.spec.type as string)}`}
                  className={classes.metadataValueLink}
                >
                  {entity.spec.type as string}
                </Link>
              </div>
            )}

            {entity.spec?.owner && (
              <div className={classes.metadataItem}>
                <div className={classes.metadataHeader}>
                  <OwnerIcon />
                  <span className={classes.metadataLabel}>Owner</span>
                </div>
                <OwnerLink
                  owner={entity.spec.owner as string}
                  color={c.primary.main}
                  style={{
                    flex: 1,
                    fontSize: 14,
                    lineHeight: '20px',
                    fontFamily: typography.fontFamilyCorpoSText,
                  }}
                />
              </div>
            )}

            {entity.spec?.domain && (
              <div className={classes.metadataItem}>
                <div className={classes.metadataHeader}>
                  <DomainIcon />
                  <span className={classes.metadataLabel}>Domain</span>
                </div>
                <span className={classes.metadataValue}>
                  {entity.spec.domain as string}
                </span>
              </div>
            )}
          </div>

          {tags.length > 0 && (
            <div className={classes.tagsSection}>
              <div className={classes.tagsHeader}>
                <TagIcon />
                <span className={classes.metadataLabel}>Tags</span>
              </div>
              <div className={classes.tagsRow}>
                {tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="medium"
                    className={classes.tag}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ---- Technical Insights ---- */}
        <div className={classes.card}>
          <h3 className={classes.cardTitle}>Technical Insights</h3>
          {INSIGHTS.map((category, catIdx) => (
            <Fragment key={`${category.heading}-${catIdx}`}>
              <p className={classes.categoryHeading}>{category.heading}</p>
              {category.items.map(item => {
                const cfg = STATUS_CONFIG[item.status];
                return (
                  <div key={item.name} className={classes.insightRow}>
                    <span className={classes.insightName}>{item.name}</span>
                    <span className={classes.statusInline}>
                      <span className={classes.statusLabel}>{cfg.label}</span>
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: cfg.color }}
                      />
                    </span>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      {/* ---- Team Members ---- */}
      {members.length > 0 &&
        (() => {
          const hasMore = totalMemberCount > MAX_MEMBERS_DISPLAY;
          const visibleMembers = members.slice(0, MAX_MEMBERS_DISPLAY);
          const remainingCount = totalMemberCount - MAX_MEMBERS_DISPLAY;

          const ownerRef = entity.spec?.owner as string | undefined;
          let ownerPath = '/catalog';
          if (ownerRef) {
            try {
              const parsed = parseEntityRef(ownerRef, {
                defaultKind: 'group',
                defaultNamespace: 'default',
              });
              ownerPath = `/catalog/${parsed.namespace}/${parsed.kind.toLowerCase()}/${parsed.name}`;
            } catch {
              // ignore parse errors
            }
          }

          return (
            <div className={classes.card}>
              <h3 className={classes.cardTitle}>Team Members</h3>
              <div className={classes.teamGrid}>
                {visibleMembers.map(member => {
                  const name = member.metadata.name;
                  const displayName =
                    (member.spec?.profile as { displayName?: string })
                      ?.displayName ?? name;
                  const role =
                    (member.spec?.profile as { role?: string })?.role ?? '';
                  const ns = member.metadata.namespace ?? 'default';
                  return (
                    <Link
                      key={stringifyEntityRef(member)}
                      className={classes.memberCard}
                      tabIndex={0}
                      to={`/catalog/${ns}/user/${name}`}
                    >
                      <div
                        className={classes.avatar}
                        style={{ backgroundColor: AVATAR_COLOR }}
                      >
                        {getInitials(displayName)}
                      </div>
                      <div className={classes.memberInfo}>
                        <div className={classes.memberName}>{displayName}</div>
                        {role && (
                          <div className={classes.memberRole}>{role}</div>
                        )}
                      </div>
                    </Link>
                  );
                })}
                {hasMore && (
                  <Link
                    className={classes.moreButton}
                    to={ownerPath}
                    tabIndex={0}
                  >
                    <div className={classes.moreAvatar}>...</div>
                    <span className={classes.moreText}>
                      +{remainingCount} more
                    </span>
                  </Link>
                )}
              </div>
            </div>
          );
        })()}
    </div>
  );
};
